/**
 * Site header - MD-31 ASKLAW-EMBER-THEME Phase 1 (v5).
 *
 * Glassmorphic backdrop (paper-overlay 85% + saturate 150% + blur 10px).
 * 4 pill nav button (Hỏi đáp pháp luật / Tra cứu pháp luật / Tin pháp luật /
 * Cẩm nang pháp luật) per §6.1 - renamed 2026-06-12 per DECISIONS
 * s2026-06-12-NAV-LABELS-LEGAL-FLAVOR (UPL: label CAM chua "Tư vấn pháp luật").
 * Active route: bg accent-soft + color accent-deep.
 * Collapsed label 720-1279px ("Hỏi đáp" / "Tra cứu" / "Tin pháp luật" / "Cẩm nang")
 * - label moi dai hon nen range collapse mo rong tu 1023 len 1279.
 * Mobile < 720px: hamburger drawer slide-right 280px voi 4 nav vertical stack (full label).
 * ThemeToggle inline next to LangSwitcher.
 * Auth CTA giu logic isAuthenticated (avatar + dropdown post-login).
 *
 * Per DECISIONS s2026-05-27-UI-REDESIGN-V5-ASKLAW-EMBER-GEMINI-INSPIRED-THEME-SWAP.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../use-auth';
import { useLang } from '../use-lang';
import { LangSwitcher } from './lang-switcher';
import { ThemeToggle } from './theme-toggle';

type NavItem = {
  to: string;
  label_vi: string;
  label_vi_tablet: string; // 720-1023px collapse label
  label_en: string;
  label_en_tablet: string;
  label_vi_rail: string; // 72px NavRail + BottomTabBar label (app shell 2026-06-12)
  label_en_rail: string;
};

// Exported for spec guard test (nav-labels.test.ts, F-FE-THEME-GEMINI §6.1).
export const NAV: NavItem[] = [
  {
    to: '/chat',
    label_vi: 'Hỏi đáp pháp luật',
    label_vi_tablet: 'Hỏi đáp',
    label_en: 'Legal Q&A',
    label_en_tablet: 'Ask',
    label_vi_rail: 'Hỏi đáp',
    label_en_rail: 'Ask',
  },
  {
    to: '/tim-kiem',
    label_vi: 'Tra cứu pháp luật',
    label_vi_tablet: 'Tra cứu',
    label_en: 'Legal documents',
    label_en_tablet: 'Search',
    label_vi_rail: 'Tra cứu',
    label_en_rail: 'Search',
  },
  {
    to: '/news',
    label_vi: 'Tin pháp luật',
    label_vi_tablet: 'Tin pháp luật',
    label_en: 'Legal news',
    label_en_tablet: 'News',
    label_vi_rail: 'Tin tức',
    label_en_rail: 'News',
  },
  {
    to: '/blog',
    label_vi: 'Cẩm nang pháp luật',
    label_vi_tablet: 'Cẩm nang',
    label_en: 'Legal guides',
    label_en_tablet: 'Guides',
    label_vi_rail: 'Cẩm nang',
    label_en_rail: 'Guides',
  },
];

const MOBILE_BREAKPOINT = 720;
// 1280 (not 1024) since 2026-06-12: 4 full label moi (~65 ky tu) chi fit >= 1280px.
const TABLET_BREAKPOINT = 1280;

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, profile, logout } = useAuth();
  const { lang } = useLang();
  // Hydration safety: SSG runs under jsdom (innerWidth=1024) but the real
  // client viewport may be <720 (mobile). Always SSR the desktop tree
  // (isMobile=false, isTablet=false) and only flip after mount.
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Phase 1.5: logo entrance fade once on first mount only (NOT on
  // navigate). useState init flag flipped via useEffect once.
  const [logoMounted, setLogoMounted] = useState(false);

  useEffect(() => {
    function compute() {
      if (typeof window === 'undefined') return;
      const w = window.innerWidth;
      const mobile = w < MOBILE_BREAKPOINT;
      const tablet = w >= MOBILE_BREAKPOINT && w < TABLET_BREAKPOINT;
      setIsMobile(mobile);
      setIsTablet(tablet);
      if (!mobile) setMenuOpen(false);
    }
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Phase 1.5: fire logo entrance fade once on first mount.
  useEffect(() => {
    setLogoMounted(true);
  }, []);

  // Body scroll lock when mobile drawer open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [menuOpen]);

  const t = {
    brandAria: lang === 'en' ? 'Asklaw - back to home' : 'Asklaw - về trang chủ',
    primary: lang === 'en' ? 'Primary' : 'Primary',
    primaryMobile: lang === 'en' ? 'Primary mobile' : 'Điều hướng mobile',
    account: lang === 'en' ? 'Account' : 'Tài khoản',
    signOut: lang === 'en' ? 'Sign out' : 'Đăng xuất',
    signIn: lang === 'en' ? 'Sign in' : 'Đăng nhập',
    signUp: lang === 'en' ? 'Sign up' : 'Đăng ký',
    openMenu: lang === 'en' ? 'Open menu' : 'Mở menu',
    closeMenu: lang === 'en' ? 'Close menu' : 'Đóng menu',
  };

  const emailUser = profile?.email ? profile.email.split('@')[0] : '';
  const displayName = profile?.name?.trim() || emailUser || t.account;
  const initial = (profile?.name?.[0] ?? profile?.email?.[0] ?? '?').toUpperCase();

  function pickLabel(item: NavItem): string {
    if (lang === 'en') return isTablet ? item.label_en_tablet : item.label_en;
    return isTablet ? item.label_vi_tablet : item.label_vi;
  }

  return (
    <header
      role="banner"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        background: 'var(--asklaw-header-bg)',
        backdropFilter: 'saturate(150%) blur(10px)',
        WebkitBackdropFilter: 'saturate(150%) blur(10px)',
        borderBottom: '1px solid var(--asklaw-border)',
        padding: '0 20px',
        height: isMobile ? 56 : 64,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <Link
          to="/"
          aria-label={t.brandAria}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            textDecoration: 'none',
            color: 'var(--asklaw-ink)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 19,
            letterSpacing: '-0.02em',
          }}
        >
          <BrandMark entrance={logoMounted} />
          Asklaw
        </Link>

        {!isMobile ? (
          <nav
            aria-label={t.primary}
            style={{
              display: 'flex',
              gap: 4,
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {NAV.map((n) => {
              const active = location.pathname === n.to || location.pathname.startsWith(n.to + '/');
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    color: active ? 'var(--asklaw-accent-deep)' : 'var(--asklaw-ink-soft)',
                    background: active ? 'var(--asklaw-accent-soft)' : 'transparent',
                    textDecoration: 'none',
                    fontWeight: active ? 600 : 500,
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-full)',
                    transition:
                      'background var(--motion-fast, 140ms) ease, color var(--motion-fast, 140ms) ease',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = 'var(--asklaw-paper-sunk)';
                      e.currentTarget.style.color = 'var(--asklaw-ink)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--asklaw-ink-soft)';
                    }
                  }}
                >
                  {pickLabel(n)}
                </Link>
              );
            })}
          </nav>
        ) : null}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ThemeToggle />
          {!isMobile ? <LangSwitcher /> : null}
          {isMobile ? (
            <button
              type="button"
              aria-label={menuOpen ? t.closeMenu : t.openMenu}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen((v) => !v)}
              style={{
                width: 36,
                height: 36,
                border: '1px solid var(--asklaw-border)',
                borderRadius: 'var(--radius-full)',
                background: 'transparent',
                color: 'var(--asklaw-ink)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <HamburgerIcon open={menuOpen} />
            </button>
          ) : null}
          {!isMobile && isAuthenticated ? (
            <>
              <Link
                to="/me"
                aria-label={t.account}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--asklaw-ink)',
                  textDecoration: 'none',
                  padding: '6px 12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 'var(--radius-full)',
                }}
              >
                {profile?.picture ? (
                  <img
                    src={profile.picture}
                    alt=""
                    width={28}
                    height={28}
                    referrerPolicy="no-referrer"
                    style={{ borderRadius: '50%', display: 'block', flexShrink: 0 }}
                  />
                ) : (
                  <span
                    aria-hidden
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'var(--asklaw-accent)',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {initial}
                  </span>
                )}
                <span
                  style={{
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </span>
              </Link>
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--asklaw-ink)',
                  background: 'transparent',
                  border: '1px solid var(--asklaw-border-strong)',
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-full)',
                  cursor: 'pointer',
                }}
              >
                {t.signOut}
              </button>
            </>
          ) : null}
          {!isMobile && !isAuthenticated ? (
            <>
              <Link
                to="/auth/login"
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--asklaw-ink)',
                  textDecoration: 'none',
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-full)',
                }}
              >
                {t.signIn}
              </Link>
              <Link
                to="/auth/signup"
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  background: 'var(--asklaw-gradient)',
                  textDecoration: 'none',
                  padding: '8px 18px',
                  borderRadius: 'var(--radius-full)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                {t.signUp}
                <svg className="arrow" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                  <path d="M2.5 8h9.5M8.5 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </>
          ) : null}
        </div>
      </div>

      {/* Mobile drawer - slide right 280px */}
      {isMobile && menuOpen ? (
        <>
          {/* Backdrop */}
          <button
            type="button"
            aria-label={t.closeMenu}
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 56,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 17, 21, 0.40)',
              zIndex: 39,
              border: 0,
              cursor: 'pointer',
              padding: 0,
            }}
          />
          <nav
            id="mobile-menu"
            aria-label={t.primaryMobile}
            style={{
              position: 'fixed',
              top: 56,
              right: 0,
              bottom: 0,
              width: 280,
              maxWidth: '85vw',
              background: 'var(--asklaw-paper-elev)',
              borderLeft: '1px solid var(--asklaw-border)',
              padding: '16px 20px 24px',
              zIndex: 41,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontFamily: 'var(--font-sans)',
              fontSize: 15,
              overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            {NAV.map((n) => {
              const active = location.pathname === n.to || location.pathname.startsWith(n.to + '/');
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    padding: '12px 14px',
                    color: active ? 'var(--asklaw-accent-deep)' : 'var(--asklaw-ink)',
                    background: active ? 'var(--asklaw-accent-soft)' : 'transparent',
                    textDecoration: 'none',
                    fontWeight: active ? 600 : 500,
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  {pickLabel(n)}
                </Link>
              );
            })}
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--asklaw-border)',
              }}
            >
              <LangSwitcher variant="compact" />
            </div>
            <div style={{ marginTop: 'auto', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!isAuthenticated ? (
                <>
                  <Link
                    to="/auth/login"
                    style={{
                      padding: '10px 14px',
                      color: 'var(--asklaw-ink)',
                      textDecoration: 'none',
                      fontWeight: 600,
                      border: '1px solid var(--asklaw-border-strong)',
                      borderRadius: 'var(--radius-full)',
                      textAlign: 'center',
                    }}
                  >
                    {t.signIn}
                  </Link>
                  <Link
                    to="/auth/signup"
                    style={{
                      padding: '12px 14px',
                      color: '#fff',
                      background: 'var(--asklaw-gradient)',
                      textDecoration: 'none',
                      fontWeight: 600,
                      borderRadius: 'var(--radius-full)',
                      textAlign: 'center',
                      boxShadow: 'var(--shadow-sm)',
                    }}
                  >
                    {t.signUp}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/me"
                    style={{
                      padding: '10px 14px',
                      color: 'var(--asklaw-ink)',
                      textDecoration: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {t.account}
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                    style={{
                      padding: '10px 14px',
                      background: 'transparent',
                      border: '1px solid var(--asklaw-border-strong)',
                      textAlign: 'center',
                      fontFamily: 'inherit',
                      fontSize: 15,
                      fontWeight: 600,
                      color: 'var(--asklaw-ink)',
                      cursor: 'pointer',
                      borderRadius: 'var(--radius-full)',
                    }}
                  >
                    {t.signOut}
                  </button>
                </>
              )}
            </div>
          </nav>
        </>
      ) : null}
    </header>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {open ? (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      )}
    </svg>
  );
}

// Letter A outer + inner V, traced as one closed path. Shared by the
// visible fill and the sheen mask so the two can never drift apart.
const A_MARK_PATH = 'M 32 8 L 57 56 L 47 56 L 32 28 L 17 56 L 7 56 Z';
// 4-point spark inside the A interior.
const A_SPARK_PATH =
  'M 24 37 L 25.6 42.4 L 31 44 L 25.6 45.6 L 24 51 L 22.4 45.6 L 17 44 L 22.4 42.4 Z';

function BrandMark({ entrance = false }: { entrance?: boolean }) {
  // Asklaw A-mark: gradient orange letter A with 4-point spark inside.
  // Matches favicon.svg + logo-1000.png + logo-horizontal.png.
  // Phase 1.5: entrance prop -> add .logo-entrance class on first mount
  // -> CSS keyframes asklaw-logo-entrance fade 400ms.
  // 2026-06-27: metallic sheen. A bright warm-white band (askSheenGrad)
  // sweeps diagonally across the mark, clipped to the letterform via
  // askMarkMask so the highlight only glints on the A + spark, never the
  // background. Animated by CSS class .brand-sheen (translateX sweep);
  // disabled under prefers-reduced-motion. Brand gradient + shape locked.
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 64 64"
      aria-hidden
      className={'header-logo-mark' + (entrance ? ' logo-entrance' : '')}
    >
      <defs>
        <linearGradient id="askMarkGrad" x1="80%" y1="0%" x2="10%" y2="100%">
          <stop offset="0%" stopColor="#FFB070" />
          <stop offset="55%" stopColor="#F47A2A" />
          <stop offset="100%" stopColor="#D9461B" />
        </linearGradient>
        {/* Sheen band: transparent -> warm white -> transparent, tilted
            (x2/y2 = 1/0.5) so the highlight reads as a diagonal glint. */}
        <linearGradient id="askSheenGrad" x1="0" y1="0" x2="1" y2="0.5">
          <stop offset="0.34" stopColor="#FFF6E8" stopOpacity="0" />
          <stop offset="0.5" stopColor="#FFF6E8" stopOpacity="0.9" />
          <stop offset="0.66" stopColor="#FFF6E8" stopOpacity="0" />
        </linearGradient>
        {/* Mask = letterform (white visible, black hidden). Confines sheen. */}
        <mask id="askMarkMask">
          <path d={A_MARK_PATH} fill="#fff" />
          <path d={A_SPARK_PATH} fill="#fff" />
        </mask>
      </defs>
      <rect width="64" height="64" rx="14" fill="#0E1218" />
      <path
        d={A_MARK_PATH}
        fill="url(#askMarkGrad)"
        stroke="url(#askMarkGrad)"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d={A_SPARK_PATH} fill="url(#askMarkGrad)" />
      <g mask="url(#askMarkMask)">
        <rect
          className="brand-sheen"
          width="64"
          height="64"
          fill="url(#askSheenGrad)"
        />
      </g>
    </svg>
  );
}
